/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import pickBy from 'lodash/pickBy';
import omit from 'lodash/omit';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import toNumber from 'lodash/toNumber';
import trim from 'lodash/trim';

export const JSON_SCHEMA_DATATYPES = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  null: 'null',
  object: 'object',
  array: 'array',
};

// needed since we treat null as a special type
// in the UI that must be specified via the "allow null" check in "validation constraints" modal
// this constant saves us having to filter out null at runtime
export const JSON_SCHEMA_DATATYPES_EXCLUDING_NULL = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  object: 'object',
  array: 'array',
};

export const isNullType = t => t === JSON_SCHEMA_DATATYPES.null;
export const isNotNullType = t => t !== JSON_SCHEMA_DATATYPES.null;

export const unwrapSingletonArray = t => (isArray(t) ? (t.length === 1 ? t[0] : t) : t);

export const ensureArray = t => (isNil(t) ? [] : isArray(t) ? t : [t]);

export const ROW_ACTION_IDS = {
  ADD_PROPERTY: 'ADD_PROPERTY',
  REMOVE_PROPERTY: 'REMOVE_PROPERTY',
  OPEN_VALIDATION_OPTIONS_MODAL: 'OPEN_VALIDATION_OPTIONS_MODAL',
};

/*
 recursively transform properties from object to array to make it suitable for table rendering:
  Specifically:
    - properties are converted from objects to arrays, with the object key passed on as a "name" field
    - list of required property names on a parent object are translated to a required boolean on child property
    - if a property's type list includes null, map this to a flag "allowNull"

  e.g.:

  {
    properties: {
      "x": {...},
      "y": {"type": ["number", "null"], ...},
    },
    required: ["x"]
  }
  ->
  {
    properties: [
      {name: "x", required: false, allowNull: false, ...},
      {name: "y", required: true, allowNull: true, ...},
      ]
  }

  The algorithm recurses down nested objects and arrays, e.g.:

  // object
  { properties: { "root": { properties: { "leaf": {...} } } } } ->
  { properties: [ { name: "root", properties: [ {name: "leaf"} ]} ] }

  // object nested inside array
  { properties: { "array": { items: { properties: { "array.x": { type: "string" } }, required: ["array.x"] } } } } ->
  { properties: [ { name: "array", items: { properties: [ {name: "array.x", required: true} ] }} ] }

  other aspects of the schema are copied across verbatim
*/
export const transformContent = (content, parentRequired, thisName) => {
  if (isNil(content)) {
    return undefined;
  }

  const thisRequired = content.required;

  const thisType = isArray(content.type)
    ? unwrapSingletonArray(content.type.filter(isNotNullType))
    : content.type;

  const out = {
    ...content,

    required: thisName // only set required to false/true when thisName is defined (i.e. content points to an object's property)
      ? !!(parentRequired && parentRequired.some(n => n === thisName))
      : undefined,

    allowNull:
      (!isNil(content.type) && isArray(content.type) && content.type.some(isNullType)) ||
      isNullType(content.type),

    type: isNullType(thisType) ? undefined : thisType, // don't include type if just {..., "type": null} ,

    properties: content.properties
      ? Object.entries(content.properties).map(([key, value]) => ({
          name: key,
          ...transformContent(value, thisRequired, key),
        }))
      : undefined,

    items: content.items
      ? isArray(content.items)
        ? content.items.map(i => transformContent(i))
        : transformContent(content.items)
      : undefined,
  };

  // remove any keys with undefined values
  return pickBy(out, v => v !== undefined);
};

/**
 * The exact inverse of transformContent
 *
 *   e.g.:
  {
    properties: [
      {name: "x", required: false, allowNull: false, ...},
      {name: "y", required: true, allowNull: true, ...}
    ]
  }
  ->
  {
    properties: {
      "x": {...},
      "y": {"type": ["number", "null"], ...}
    },
    required: ["x"]
  }

 */
export const untransformContent = content => {
  if (isNil(content)) {
    return undefined;
  }
  let requiredChildren = [];
  const a = {
    ...content,
    allowNull: undefined,
    type: content.allowNull
      ? isNil(content.type)
        ? JSON_SCHEMA_DATATYPES.null
        : isArray(content.type)
        ? [...content.type, JSON_SCHEMA_DATATYPES.null]
        : [content.type, JSON_SCHEMA_DATATYPES.null]
      : content.type,

    properties: content.properties
      ? content.properties.reduce((acc, v) => {
          if (v.required) {
            requiredChildren = [...requiredChildren, v.name];
          }
          return {
            ...acc,
            [v.name]: omit(untransformContent(v), 'name'),
          };
        }, {})
      : undefined,

    items: content.items
      ? isArray(content.items)
        ? content.items.map(i => untransformContent(i))
        : untransformContent(content.items)
      : undefined,
  };

  const out = {
    ...a,
    required: requiredChildren.length > 0 ? requiredChildren : undefined,
  };

  // remove any keys with undefined values
  return pickBy(out, v => v !== undefined);
};

/**
 * Input transformed schema content only
 *
 */
const newPropertyRegex = /newproperty_([0-9]+)/;
export const getUniqueNewPropertyName = objectProperty => {
  if (!objectProperty || !objectProperty.properties) {
    return undefined;
  }
  // get the largest value of i where property.name="newproperty_i"
  const uniqueSuffix =
    objectProperty.properties.reduce((largest, prop) => {
      const propName = prop.name;
      const match = newPropertyRegex.exec(propName);
      if (match === null) {
        return largest;
      }
      const asInt = parseInt(match[1], 10);
      return !isNaN(asInt) && asInt > largest ? asInt : largest;
    }, 0) + 1;
  return `newproperty_${uniqueSuffix}`;
};

/**
 * Input transformed schema content only
 *
 */
export const validateSchemaContent = (node, atRoot = true, isItemsNode = false) => {
  const errors = {};

  if (!node) {
    return {};
  }

  // we're at an object node; recurse
  if (isArray(node.properties)) {
    errors.properties = node.properties.map(child => validateSchemaContent(child, false));

    // if there are no errors in any of the properties, remove it from the final error report
    if (errors.properties.every(v => isEmpty(v))) {
      errors.properties = undefined;
    }
  }
  // we're at an array node; recurse into items
  if (node.items) {
    const itemErrors = validateSchemaContent(node.items, false, true);
    if (!isEmpty(itemErrors)) {
      errors.items = itemErrors;
    }
  }

  // every node other than the root node, or items nodes, need to have a name property
  if (!atRoot && !isItemsNode && !node.name) {
    errors.name = 'name must be set';
  }

  if (!atRoot && !node.type) {
    errors.type = 'data type must be set';
  }

  // all object properties must define at least one property
  if (
    node.type === JSON_SCHEMA_DATATYPES.object &&
    (isNil(node.properties) || !isArray(node.properties) || isEmpty(node.properties))
  ) {
    // if this schema made it to the POST request, API would return:
    // "CUDIM0021E: The supplied schema cannot be used in the definition of an logical interface: Couldn't create JsonSchema from contents because: All objects must define at least one property""
    errors.type = 'All objects must define at least one property';
  }

  // and clean up any undefined fields
  return pickBy(errors, v => v !== undefined);
};

/**
 * Additional schema content validation rules that apply to Logical Interface schemas only
 * Input transformed schema content only
 *
 */
export const validateLiSchemaContent = (node, atRoot = true, isItemsNode = false) => {
  const errors = {};

  if (!node) {
    return {};
  }

  // we're at an object node; recurse
  if (isArray(node.properties)) {
    errors.properties = node.properties.map(child => validateLiSchemaContent(child, false));

    // if there are no errors in any of the properties, remove it from the final error report
    if (errors.properties.every(v => isEmpty(v))) {
      errors.properties = undefined;
    }
  }
  // we're at an array node; recurse into items
  if (node.items) {
    const itemErrors = validateLiSchemaContent(node.items, false, true);
    if (!isEmpty(itemErrors)) {
      errors.items = itemErrors;
    }
  }

  // all required primitive properties must define a default value
  if (
    !atRoot &&
    !isItemsNode &&
    node.type !== 'object' &&
    node.type !== 'array' &&
    node.required &&
    node.default === undefined
  ) {
    errors.default = 'All required LI properties must define a default value';
  }

  // and clean up any undefined fields
  return pickBy(errors, v => v !== undefined);
};

/**
 * Given a string value and a JSON schema type node value (e.g.  "string", "number", ["string", "number"], undefined, etc)
 * cast the specified string to the "most specific" type possible; precedence is:
 *  - object
 *  - array
 *  - null
 *  - boolean (only "true" or "false" - e.g. not 1 or 0)
 *  - number
 *  - string
 *
 */
export const castStringToOneOf = (str, types) => {
  if (!isString(str)) {
    throw new Error(`first parameter must be a string (supplied: ${str})`);
  }

  if (str.length === 0) {
    return undefined;
  }

  const typesArr = isNil(types) ? [] : isArray(types) ? types : [types];

  if (typesArr.includes(JSON_SCHEMA_DATATYPES.boolean)) {
    if (str === 'true') {
      return true;
    }
    if (str === 'false') {
      return false;
    }
  }

  if (typesArr.includes(JSON_SCHEMA_DATATYPES.array)) {
    try {
      const json = JSON.parse(str);
      if (isArray(json)) {
        return json;
      }
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  if (typesArr.includes(JSON_SCHEMA_DATATYPES.null) && str === 'null') {
    return null;
  }

  if (typesArr.includes(JSON_SCHEMA_DATATYPES.object)) {
    try {
      const json = JSON.parse(str);
      if (isPlainObject(json)) {
        return json;
      }
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  if (typesArr.includes(JSON_SCHEMA_DATATYPES.number) && trim(str).length > 0) {
    // prevent empty strings, and strings consisting only of whitespace from being cast
    // to 0 by toNumber
    const asNumber = toNumber(str);
    if (!isNaN(asNumber)) {
      return asNumber;
    }
  }

  // last resort: return as string (if "string" is not in the types list, a form validation error will show up)
  return str;
};

/**
 * Computes and returns an appropriate default value for a property based on
 * the supplied array of types
 */
export const generateDefault = (types, defaultValue) => {
  if (defaultValue === undefined) {
    // prevent the field from being set to the string "undefined" when switching data types
    // (this would happen since `${undefined}` -> "undefined")
    return undefined;
  }

  // TextInput seems to do some implicit casting for us, ensure we always cast back to a string
  const newDefaultValueAsString = `${defaultValue}`;

  // just ensure types in an array (in case a singlular value is passed in) to simplify algorithm
  const typesArr = ensureArray(types);

  // don't permit auto-casting to objects/arrays (for now, until we have a proper default object/array value editor)
  const filteredTypeValues = typesArr.filter(
    t => t !== JSON_SCHEMA_DATATYPES.object && t !== JSON_SCHEMA_DATATYPES.array
  );

  // don't show input if no types (or only object/array) are selected
  if (filteredTypeValues.length === 0) {
    return undefined;
  }

  // Cast the value entered by the user to one of the property's selected type(s)
  // If not possible, just pass it on as a string, form validation will take care of the rest
  return castStringToOneOf(newDefaultValueAsString, filteredTypeValues);
};

/**
 * Returns true iff input types is (or is an array that includes) type.
 *
 * e.g.
 *  object, object => true
 *  object, number => false
 *  [object, string], object => true
 *  [object, string], array => false
 *
 */
export const isOrIncludesType = (inputTypes, type) => ensureArray(inputTypes).some(t => t === type);
export const isOrIncludesArray = inputTypes =>
  isOrIncludesType(inputTypes, JSON_SCHEMA_DATATYPES.array);
export const isOrIncludesObject = inputTypes =>
  isOrIncludesType(inputTypes, JSON_SCHEMA_DATATYPES.object);
export const isOrIncludesArrayOrObject = inputTypes =>
  isOrIncludesArray(inputTypes) || isOrIncludesObject(inputTypes);

export const objectMap = (obj, func) =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => ({
      ...acc,
      ...func(key, value),
    }),
    {}
  );

export const deriveJsonSchemaType = v => {
  switch (typeof v) {
    case 'string':
      return 'string';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'object':
      if (Array.isArray(v)) {
        return 'array';
      }
      if (v === null) {
        return 'null';
      }
      return 'object';

    default:
      throw Error(`Unsupported value type ${typeof v}`);
  }
};

export const deriveJsonSchemaFromArray = arr => {
  // Based on the values in the array, determine:
  // - the set of types used
  // - if any of the valeus are objects, call buildSchemaFromObject to determine a sub-schema based on its properties
  // - if any of the values are arrays, recurse to determine a sub-schema based on its values
  const d = arr.reduce(
    ({ typez, properties, items }, a) => {
      const typeOfA = deriveJsonSchemaType(a);
      return {
        typez: typez.includes(typeOfA) ? typez : [...typez, typeOfA],
        properties:
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          typeOfA === 'object' ? [...properties, deriveJsonSchemaFromObject(a)] : properties,
        items: typeOfA === 'array' ? [...items, deriveJsonSchemaFromArray(a)] : items,
      };
    },
    { typez: [], properties: [], items: [] }
  );

  if (d.typez.length > 1) {
    // TODO: this needs to be flagged as an error in the syntax error prior to submission
    throw Error('WIoTP does not support polymorphic array types in schemas');
    // alternatively, leave the item type unspecified in this case?
  }

  // TODO: merge types of any common properties
  // e.g. {arr: [{x:1}, {x:"2"}]} ->
  // {"arr":{"type":"array","items":{"type":"object"},"properties":[{"x":{"type":"number"}},{"x":{"type":"string"}}]}} ->
  // {"arr":{"type":"array","items":{"type":"object"},"properties":[{"x":{"type":["number", "string"]}}]}}

  const output = {
    type: 'array',
  };

  const itemTypes = d.typez.length === 1 ? d.typez[0] : d.typez;
  if (itemTypes.length > 0) {
    output.items = { type: itemTypes };
  }

  if (d.properties.length > 0) {
    output.properties = d.properties;
  }

  if (d.items.length > 0) {
    output.items = d.items;
  }

  return output;
};

export const deriveJsonSchemaFromObject = o =>
  objectMap(o, (k, item) => {
    const type = deriveJsonSchemaType(item);
    let n;

    if (type === 'object') {
      n = {
        type: 'object',
        properties: deriveJsonSchemaFromObject(item),
      };
    } else if (type === 'array') {
      n = deriveJsonSchemaFromArray(item);
    } else {
      n = {
        type,
      };
    }

    return { [k]: n };
  });

export const deriveJsonSchema = (obj, root = true) => ({
  ...(root ? { $schema: 'http://json-schema.org/draft-04/schema#' } : {}),
  type: 'object',
  properties: deriveJsonSchemaFromObject(obj),
});

/*
  Builds a schema that will match any value from a device resource, including metadata fields defined (those defined at the type level only).
  Currently used to generate property suggestion and validate property references under $instance. in the MappingsEditor
  Standard device fields do not change, example document, device type-level metadata varies, so must be specified. Pass in the RAW json, and this method will derive the metadata sub-schema from it,\

  e.g. deviceTypeMetadata =
  {
    thresholds: {
      temperature: 22,
      color: [65, 33, 76]
    }
  }

  ->

  {
    ... standard device resource fields ...,
    metadata: {
      type: "object",
      properties:{
        temperature: { type: "number" },
        color: {
          type: "array",
          items: { type: "number" }
        }
      }
    }
  }
*/
export const deriveDollarInstanceJsonSchema = deviceTypeMetadata => ({
  $schema: 'http://json-schema.org/draft-04/schema#',
  type: 'object',
  additionalProperties: false,
  properties: {
    metadata: deriveJsonSchema(deviceTypeMetadata, false),
    clientId: { type: 'string' },
    typeId: { type: 'string' },
    deviceId: { type: 'string' },
    deviceInfo: {
      type: 'object',
      properties: {
        serialNumber: { type: 'string' },
        manufacturer: { type: 'string' },
        model: { type: 'string' },
        deviceClass: { type: 'string' },
        description: { type: 'string' },
        fwVersion: { type: 'string' },
        hwVersion: { type: 'string' },
        descriptiveLocation: { type: 'string' },
      },
    },
    registration: {
      type: 'object',
      properties: {
        auth: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            type: { type: 'string' },
          },
        },
        date: { type: 'string' },
      },
    },
    status: {
      type: 'object',
      properties: {
        enabled: { type: 'boolean' },
        timestamp: { type: 'string' },
      },
    },
    refs: {
      type: 'object',
      properties: {
        logs: { type: 'string' },
        errorCodes: { type: 'string' },
      },
      location: { type: 'string' },
      clientState: { type: 'string' },
    },
  },
});

export default {
  transformContent,
  untransformContent,
  getUniqueNewPropertyName,
  validateSchemaContent,
  validateLiSchemaContent,
  castStringToOneOf,
  generateDefault,
  isOrIncludesType,
  isOrIncludesArray,
  isOrIncludesObject,
  isOrIncludesArrayOrObject,

  objectMap,

  deriveJsonSchemaType,
  deriveJsonSchemaFromArray,
  deriveJsonSchemaFromObject,
  deriveJsonSchema,
  deriveDollarInstanceJsonSchema,
};
